import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Star, Building2, Mail, TrendingUp, Ticket, Award, AlertTriangle, Trash2 } from "lucide-react";

function StatCard({ label, value, hint, icon: Icon, accent = "#003D5C" }) {
  return (
    <div className="mgx-card flex items-start justify-between gap-4">
      <div>
        <div className="mgx-label">{label}</div>
        <div className="mgx-display text-4xl mt-2" style={{ color: accent }}>{value}</div>
        {hint && <div className="text-xs text-[#6B7280] mt-2">{hint}</div>}
      </div>
      <div className="w-10 h-10 border border-[#E5E7EB] flex items-center justify-center" style={{ color: accent }}>
        <Icon size={18} />
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cleaning, setCleaning] = useState(false);

  const load = () => api.get("/admin/analytics").then(({ data }) => setData(data)).catch((e) => setError(formatApiError(e)));

  useEffect(() => { load(); }, []);

  const cleanOrphans = async () => {
    if (!window.confirm("Eliminerai definitivamente i feedback legati ad hotel non più esistenti. Continuare?")) return;
    setCleaning(true);
    try {
      const { data: r } = await api.post("/admin/feedbacks/cleanup-orphans");
      await load();
      alert(`${r.deleted} feedback orfani eliminati.`);
    } catch (e) {
      alert(formatApiError(e));
    } finally {
      setCleaning(false);
    }
  };

  if (error) return <div className="text-[#E30613]">{error}</div>;
  if (!data) return <div className="text-[#6B7280]">Caricamento analytics...</div>;

  return (
    <div className="space-y-8" data-testid="analytics-dashboard">
      <div>
        <h2 className="mgx-display text-4xl text-[#003D5C]">Analytics globale</h2>
        <p className="text-sm text-[#6B7280] mt-2">Visione d'insieme sulla piattaforma Magniflex.</p>
      </div>

      {data.orphan_feedback_count > 0 && (
        <div
          data-testid="orphan-warning"
          className="mgx-card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-l-4"
          style={{ borderLeftColor: "#E30613" }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-[#E30613] mt-0.5 shrink-0" />
            <div>
              <div className="mgx-display text-lg text-[#003D5C]">
                {data.orphan_feedback_count} feedback orfani rilevati
              </div>
              <div className="text-sm text-[#6B7280] mt-1">
                Sono recensioni di hotel cancellati (dati di test residui). Vengono già esclusi dalle statistiche; puoi rimuoverli dal database in modo definitivo.
              </div>
            </div>
          </div>
          <button
            data-testid="cleanup-orphans-btn"
            onClick={cleanOrphans}
            disabled={cleaning}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#E30613] text-white text-xs uppercase tracking-[0.15em] font-medium hover:bg-[#B30510] transition-colors disabled:opacity-50 shrink-0"
          >
            <Trash2 size={14} />
            {cleaning ? "Pulizia..." : "Pulisci ora"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard label="Landing attive" value={data.landings_active} icon={Building2} />
        <StatCard label="Feedback totali" value={data.total_feedback} icon={Star} accent="#E30613" />
        <StatCard label="Media stelle" value={data.avg_rating || "—"} icon={Star} accent="#E30613" />
        <StatCard label="Coupon inviati" value={data.total_coupons_sent} icon={Mail} />
        <StatCard label="Coupon con match" value={data.total_coupons_matched} icon={Ticket} accent="#2E6F40" />
        <StatCard label="Commissioni" value={`€ ${data.total_commissions}`} icon={Award} accent="#E30613" />
      </div>

      <div className="mgx-card">
        <div className="mgx-label mb-4">Performance per hotel</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="per-hotel-table">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.15em] text-[#6B7280]">
                <th className="py-3 pr-4">Hotel</th>
                <th className="py-3 pr-4">Città</th>
                <th className="py-3 pr-4 text-right">Feedback</th>
                <th className="py-3 pr-4 text-right">Coupon inviati</th>
                <th className="py-3 pr-4 text-right">Ordini match</th>
                <th className="py-3 pr-4 text-right">Commissioni</th>
              </tr>
            </thead>
            <tbody>
              {data.per_hotel.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-[#6B7280]">Nessun hotel configurato</td></tr>
              )}
              {data.per_hotel.map((h) => (
                <tr key={h.id} className="border-b border-[#E5E7EB]/50">
                  <td className="py-3 pr-4 mgx-display text-lg text-[#003D5C]">{h.name}</td>
                  <td className="py-3 pr-4 text-[#6B7280]">{h.city}</td>
                  <td className="py-3 pr-4 text-right">{h.feedback_count}</td>
                  <td className="py-3 pr-4 text-right">{h.coupons_sent}</td>
                  <td className="py-3 pr-4 text-right">{h.matched_orders}</td>
                  <td className="py-3 pr-4 text-right text-[#E30613] mgx-display text-lg">€ {h.commission_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
